import type { HttpClient, RequestOptions } from '../../core/http';
import { paginate } from '../../core/pagination';
import type { Anonymity, Protocol } from '../../core/types';
import type {
  FreeProxy,
  FreeProxyApiResponse,
  FreeProxyFilters,
  FreeProxyListParams,
  FreeProxyListResult,
  FreeProxyRaw,
} from './types';

/**
 * Wraps the v4 free-proxy-list endpoints. No auth needed — works against
 * https://api.proxyscrape.com/v4/free-proxy-list/get .
 *
 * Three shapes:
 *   - list(params)   — one page, returns an array + pagination metadata
 *   - stream(params) — async iterator that pages through everything
 *   - random(params) — convenience for "give me one good proxy"
 *
 * The text and CSV export endpoints are exposed via `listText` / `listCsv`
 * for callers who don't want to round-trip through JSON.
 */
export class FreeProxyListClient {
  private static readonly PATH = '/free-proxy-list/get';
  private static readonly MAX_PAGE_SIZE = 2000;

  constructor(private readonly http: HttpClient) {}

  /** Fetch a single page of proxies. */
  async list(
    params: FreeProxyListParams = {},
    opts: RequestOptions = {},
  ): Promise<FreeProxyListResult> {
    const page = await this.fetchPage(params, opts);
    return {
      proxies: page.proxies.map(flattenProxy),
      shownRecords: page.shown_records,
      totalRecords: page.total_records,
      hasMore: page.nextpage,
    };
  }

  /**
   * Iterate every matching proxy across all pages. Stops automatically when
   * the API reports `nextpage: false`. Yields one proxy at a time so callers
   * can break out early without fetching the whole pool.
   */
  async *stream(
    params: FreeProxyFilters = {},
    opts: RequestOptions = {},
  ): AsyncGenerator<FreeProxy, void, void> {
    yield* paginate<FreeProxyApiResponse, FreeProxy>({
      pageSize: FreeProxyListClient.MAX_PAGE_SIZE,
      fetchPage: (skip) =>
        this.fetchPage({ ...params, limit: FreeProxyListClient.MAX_PAGE_SIZE, skip }, opts),
      extractItems: (page) => page.proxies.map(flattenProxy),
      hasMore: (page) => page.nextpage,
    });
  }

  /**
   * Return a single proxy matching the filters, or null if no matches exist.
   * Picks the first one the API returns — combine with filters (uptime,
   * country, anonymity) to narrow the pool before calling.
   */
  async random(
    params: FreeProxyFilters = {},
    opts: RequestOptions = {},
  ): Promise<FreeProxy | null> {
    // limit=1 is the cheapest way to ask "do any exist" + "give me one".
    const page = await this.fetchPage({ ...params, limit: 1 }, opts);
    const first = page.proxies[0];
    return first ? flattenProxy(first) : null;
  }

  /**
   * Plain-text export — one line per proxy as `protocol://ip:port`. Hands
   * back the raw body string so callers can pipe it straight to a file or
   * feed it line-by-line into another tool.
   */
  async listText(params: FreeProxyFilters = {}, opts: RequestOptions = {}): Promise<string> {
    return this.http.getText(
      FreeProxyListClient.PATH,
      buildQuery({
        ...params,
        request: 'display_proxies',
        proxy_format: 'protocolipport',
        format: 'text',
      }),
      opts,
    );
  }

  /** CSV export — full per-proxy fields, header row included. */
  async listCsv(params: FreeProxyFilters = {}, opts: RequestOptions = {}): Promise<string> {
    return this.http.getText(
      FreeProxyListClient.PATH,
      buildQuery({
        ...params,
        request: 'display_proxies',
        proxy_format: 'protocolipport',
        format: 'csv',
      }),
      opts,
    );
  }

  private async fetchPage(
    params: FreeProxyListParams,
    opts: RequestOptions,
  ): Promise<FreeProxyApiResponse> {
    return this.http.getJson<FreeProxyApiResponse>(
      FreeProxyListClient.PATH,
      buildQuery({
        ...params,
        request: 'get_proxies',
        proxy_format: 'protocolipport',
        format: 'json',
      }),
      opts,
    );
  }
}

// --- helpers ---------------------------------------------------------------

/** Build the query-string object the API expects from our public filter shape. */
function buildQuery(
  params: FreeProxyListParams & { request: string; proxy_format: string; format: string },
): Record<string, string | number | undefined | null> {
  return {
    request: params.request,
    proxy_format: params.proxy_format,
    format: params.format,
    protocol: arr(params.protocol)?.join(',')?.toLowerCase(),
    country: arr(params.country)
      ?.map((c) => c.toLowerCase())
      .join(','),
    anonymity: arr(params.anonymity)?.join(','),
    timeout: params.maxTimeoutMs,
    uptime: params.minUptimePercent,
    ssl: params.httpsOnly === true ? 'yes' : params.httpsOnly === false ? 'no' : undefined,
    port: arr(params.port)?.join(','),
    limit: params.limit,
    skip: params.skip,
  };
}

function arr<T>(v: T | T[] | undefined): T[] | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v : [v];
}

function flattenProxy(p: FreeProxyRaw): FreeProxy {
  const ip = p.ip;
  const port = p.port;
  const protocol = (p.protocol || '').toLowerCase() as Protocol;
  const asField = p.ip_data?.as ?? '';
  const asn = asField.startsWith('AS') ? asField.split(' ', 1)[0]! : '';

  return {
    protocol,
    ip,
    port,
    url: `${protocol}://${ip}:${port}`,
    countryCode: (p.ip_data?.countryCode ?? '').toUpperCase(),
    country: p.ip_data?.country ?? '',
    city: p.ip_data?.city ?? '',
    anonymity: (p.anonymity as Anonymity) || 'unknown',
    ssl: Boolean(p.ssl),
    uptimePercent: typeof p.uptime === 'number' ? Math.round(p.uptime * 100) / 100 : null,
    asn,
    isp: p.ip_data?.isp ?? '',
    latencyMs: typeof p.timeout === 'number' ? Math.round(p.timeout * 100) / 100 : null,
    lastCheckedAt:
      typeof p.last_seen === 'number' ? new Date(p.last_seen * 1000).toISOString() : null,
    raw: p,
  };
}
